/**
 * The task score, as the contract shows it: a ring and "N details missing",
 * never a number (owner decision 2026-09-04). The number still drives the arc
 * so a fuller ring reads as "closer to plannable" at a glance.
 */
import type { TaskScore } from '../../services/taskService'
import { missingCaption } from '../../utils/taskForm'

interface Props {
  score: TaskScore | undefined | null
  /** Pixel size of the ring. 18 on a row, 44 on the detail card. */
  size?: number
  /** Show the "N details missing" caption beside the ring. */
  caption?: boolean
  /** Text class for the caption; rows pass text-app-micro. */
  captionClass?: string
  className?: string
}

export function TaskScoreRing({ score, size = 18, caption = false, captionClass = 'text-xs', className }: Props) {
  if (!score || !score.scored || score.score == null) return null
  const stroke = Math.max(2, Math.round(size / 9))
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const pct = Math.max(0, Math.min(100, score.score))
  const dash = (pct / 100) * c
  const done = score.missing.length === 0
  const label = missingCaption(score)
  return (
    <span className={`inline-flex items-center gap-1.5 ${className ?? ''}`} title={label ?? undefined}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" className="shrink-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-soft" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${dash} ${c - dash}`}
          className={done ? 'text-ok' : 'text-brand'}
        />
      </svg>
      {caption && label && (
        <span className={`${captionClass} ${done ? 'text-ok' : 'text-muted'}`}>{label}</span>
      )}
      {!caption && label && <span className="sr-only">{label}</span>}
    </span>
  )
}
